import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CCNavbar from '../../components/CCNavbar';
import CCFooter from '../../components/CCFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath } from '../../utils/brandRouting';
import { SUPPORT_EMAIL, products } from '../../data/siteContent';

const CARDCYCLE = products.find((product) => product.id === 'cardcycle');
const AVAILABILITY = 'Available on Google Play';

function useCCTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'cardcycle');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

/**
 * CardCycle product page. The public claim set is deliberately small: the
 * founder-approved catalogue description and the verified Google Play
 * destination, both from data/siteContent. Data-safety, encryption, offline,
 * permissions, and pricing statements stay in the app's legal pages — see the
 * T04 content brief.
 */
export default function CardCycle() {
  useCCTheme();
  useDocumentMeta(routeMetaFor('/apps/cardcycle'));

  return (
    <PageWrap testId="cardcycle-page">
      <CCNavbar />

      {/* HERO */}
      <section className="cc-hero" data-testid="cc-hero" style={{ minHeight: 'auto', paddingTop: 'calc(var(--nav-h) + 96px)', paddingBottom: 96 }}>
        <div className="cc-hero-orb-1"></div>
        <div className="cc-hero-orb-2"></div>
        <div className="cc-hero-grid-pattern"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow reveal" style={{ color: '#5EB7F8' }}>{AVAILABILITY}</div>
          <h1 className="reveal d1" style={{ color: '#fff', marginBottom: '18px' }}>{CARDCYCLE.name}</h1>
          <p className="lead reveal d2" style={{ color: 'rgba(255,255,255,0.78)', marginBottom: '32px', maxWidth: '560px' }}>
            {CARDCYCLE.description}
          </p>
          <div className="btn-group reveal d3">
            <a
              href={CARDCYCLE.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cc-store-btn"
              data-testid="cc-playstore-btn"
            >
              <i className="fab fa-google-play"></i>
              <div><span>Get it on</span><strong>{CARDCYCLE.storeLabel}</strong></div>
            </a>
          </div>
        </div>
      </section>

      {/* LEGAL, FAQ AND SUPPORT ROUTES */}
      <section className="cc-section-white" style={{ paddingBottom: 64 }} data-testid="cc-resources">
        <div className="wrap">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
            <div>
              <div className="eyebrow reveal">Resources</div>
              <h2 className="reveal d1">Legal &amp; support</h2>
            </div>
            <Link to={brandPath('cardcycle', '/faq')} className="btn btn-cc-outline reveal" data-testid="cc-faq-cta">All CardCycle FAQs →</Link>
          </div>
          <div className="grid-3">
            <Link to={brandPath('cardcycle', '/privacy')} className="cc-legal-card reveal" data-testid="cc-privacy-link"><i className="fas fa-shield-halved"></i><div><strong style={{ color: 'var(--cc-deep)' }}>Privacy Notice</strong><div style={{ fontSize: '0.82rem', color: 'var(--cc-muted)' }}>What stays on your device</div></div></Link>
            <Link to={brandPath('cardcycle', '/terms')} className="cc-legal-card reveal d1" data-testid="cc-terms-link"><i className="fas fa-file-contract"></i><div><strong style={{ color: 'var(--cc-deep)' }}>Terms of Use</strong><div style={{ fontSize: '0.82rem', color: 'var(--cc-muted)' }}>How CardCycle works for you</div></div></Link>
            <Link to={brandPath('cardcycle', '/faq')} className="cc-legal-card reveal d2" data-testid="cc-faq-link"><i className="fas fa-circle-question"></i><div><strong style={{ color: 'var(--cc-deep)' }}>FAQ</strong><div style={{ fontSize: '0.82rem', color: 'var(--cc-muted)' }}>Common questions answered</div></div></Link>
            <Link to={brandPath('cardcycle', '/account-deletion')} className="cc-legal-card reveal d3" data-testid="cc-deletion-link"><i className="fas fa-trash-can"></i><div><strong style={{ color: 'var(--cc-deep)' }}>Data Deletion</strong><div style={{ fontSize: '0.82rem', color: 'var(--cc-muted)' }}>Erase CardCycle data</div></div></Link>
          </div>
          <p className="reveal d3" style={{ marginTop: 28, fontSize: '0.9rem', color: 'var(--cc-muted)' }}>
            Questions about CardCycle? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--cc-primary)', fontWeight: 600 }} data-testid="cc-support-email">{SUPPORT_EMAIL}</a>.
          </p>
        </div>
      </section>

      <CCFooter />
    </PageWrap>
  );
}
